import { type FC, useRef } from "react";

import type { Task } from "@recobra/schemas/task";
import { Dialog, DialogContent } from "@recobra/ui/dialog";

import { useAuth } from "../lib";
import { useContextEventInvalidation } from "./task-data";
import { useUpdateTaskMutation } from "./task-data";
import { TaskModalPanel } from "./task-modal";
import { useTaskModalData } from "./task-modal";

export const TaskModal: FC<{
  taskId: string | null;
  placeholderTask?: Task;
  /** Hides every metadata editor, leaving only the affordances that execute the task. */
  executionOnly?: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
}> = ({ taskId, placeholderTask, executionOnly = false, onOpenChange, onCompleted }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { task, error, executionState, contextRoute } = useTaskModalData({
    taskId,
    placeholderTask,
  });
  useContextEventInvalidation({
    contextType: task?.contextType ?? "case",
    contextId: task?.contextId,
    caseNumber: task?.contextCaseNumber,
  });

  const updateTaskMutation = useUpdateTaskMutation({
    username: user.username,
    contextType: task?.contextType,
    contextId: task?.contextId,
    onSuccess: ({ input }) => {
      if ("transition" in input || input.status !== "done") return;
      onCompleted?.();
      onOpenChange(false);
    },
  });
  return (
    <Dialog
      open={!!taskId}
      onOpenChange={(open) => {
        if (!open && updateTaskMutation.isSubmitting) return;
        onOpenChange(open);
      }}
    >
      <DialogContent ref={contentRef} initialFocus={contentRef} className="sm:max-w-2xl">
        <TaskModalPanel
          task={task}
          error={error}
          executionState={executionState}
          executionOnly={executionOnly}
          contextRoute={contextRoute}
          isUpdating={updateTaskMutation.isSubmitting}
          onProgress={(id, input) => updateTaskMutation.mutateProgress(id, input)}
          onStatusChange={(id, status, waitingStatus) =>
            updateTaskMutation.mutate({
              id,
              input: { status, ...(waitingStatus ? { waitingStatus } : {}) },
            })
          }
          onConfirm={(id, input) => updateTaskMutation.mutate({ id, input })}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
